// The kept answers' storage in the browser: IndexedDB (`kept.ts` has the
// rules). IndexedDB rather than localStorage because an answer can be a few
// hundred kilobytes and two hundred of them are kept, which is past what
// localStorage allows a site, and because it does not block the page while it
// writes.
//
// One object store, keyed by the account and the path, with an index on the
// order each was written in, so the oldest can be trimmed without reading the
// bodies. The order is a counter kept in the rows, not a time: this layer
// reads no clock (the lint on `data/`), and the order is all trimming needs.

import { createKept, type KeptCopy, type KeptStorage } from './kept';

const NAME = 'fsp-kept';
const VERSION = 1;
const ANSWERS = 'answers';
const BY_ORDER = 'byOrder';

interface Row {
  key: string;
  data: unknown;
  asOf: string;
  order: number;
}

function done<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
}

function finished(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
  });
}

/** The browser's storage, or null where it has none (a test, a server, a locked-down browser). */
export function openKeptStorage(): KeptStorage | null {
  if (typeof indexedDB === 'undefined') return null;
  // Opened on first use and kept open. One that failed stays failed for the
  // page, and every call rejects, which `kept.ts` swallows.
  let opened: Promise<IDBDatabase> | null = null;
  const database = (): Promise<IDBDatabase> =>
    (opened ??= new Promise((resolve, reject) => {
      const request = indexedDB.open(NAME, VERSION);
      request.onupgradeneeded = () => {
        const answers = request.result.createObjectStore(ANSWERS, { keyPath: 'key' });
        answers.createIndex(BY_ORDER, 'order');
      };
      request.onsuccess = () => {
        const db = request.result;
        // A newer version opened in another tab: let it upgrade.
        db.onversionchange = () => db.close();
        resolve(db);
      };
      request.onerror = () => reject(request.error ?? new Error('IndexedDB would not open'));
      request.onblocked = () => reject(new Error('IndexedDB is held open by another tab'));
    }));

  return {
    async get(key) {
      const db = await database();
      const row = (await done(db.transaction(ANSWERS).objectStore(ANSWERS).get(key))) as
        Row | undefined;
      return row === undefined ? null : ({ data: row.data, asOf: row.asOf } satisfies KeptCopy);
    },

    async put(key, copy) {
      const db = await database();
      const tx = db.transaction(ANSWERS, 'readwrite');
      const answers = tx.objectStore(ANSWERS);
      // The next in order, read in the same transaction as the write.
      const last = answers.index(BY_ORDER).openKeyCursor(null, 'prev');
      last.onsuccess = () => {
        const order = last.result === null ? 1 : Number(last.result.key) + 1;
        answers.put({ key, data: copy.data, asOf: copy.asOf, order } satisfies Row);
      };
      await finished(tx);
    },

    async delete(key) {
      const db = await database();
      const tx = db.transaction(ANSWERS, 'readwrite');
      tx.objectStore(ANSWERS).delete(key);
      await finished(tx);
    },

    async trim(capacity) {
      const db = await database();
      const tx = db.transaction(ANSWERS, 'readwrite');
      const answers = tx.objectStore(ANSWERS);
      const count = answers.count();
      count.onsuccess = () => {
        let over = count.result - capacity;
        if (over <= 0) return;
        const oldest = answers.index(BY_ORDER).openCursor();
        oldest.onsuccess = () => {
          const cursor = oldest.result;
          if (cursor === null || over <= 0) return;
          cursor.delete();
          over -= 1;
          cursor.continue();
        };
      };
      await finished(tx);
    },

    async clear() {
      const db = await database();
      const tx = db.transaction(ANSWERS, 'readwrite');
      tx.objectStore(ANSWERS).clear();
      await finished(tx);
    },
  };
}

/**
 * THE PAGE'S KEPT ANSWERS (B-557): the shared reads keep theirs here
 * (`read.ts`), and so do chat, the bell and a league's time zone. The session
 * says whose they are (`session.tsx`).
 */
export const onDevice = createKept({
  storage: openKeptStorage(),
  after: (ms, fn) => {
    const timer = setTimeout(fn, ms);
    return () => clearTimeout(timer);
  },
});
